/**
 * API Client für Channel Packages
 * 
 * Endpoints für Channel Package Management und Regeneration.
 */

import apiClient from '../api-client'
import type {
  ChannelPackagesResponse,
  ChannelPackageResponse,
  RegenerateResponse,
  RegenerateAllResponse,
  ResolveResponse,
  ChannelPackagesQuery,
} from '@/types/channel-packages'

const BASE_URL = '/api/v1/channels'
const PRODUCTS_URL = '/api/v1/products'

// =============================================================================
// CHANNEL PACKAGES
// =============================================================================

/**
 * Get all channel packages for a product
 */
export async function getProductChannelPackages(
  productId: string
): Promise<ChannelPackagesResponse> {
  const response = await apiClient.get<ChannelPackagesResponse>(
    `${PRODUCTS_URL}/${productId}/channel-packages`
  )
  return response.data
}

/**
 * Get channel packages with optional filters
 */
export async function getChannelPackages(
  query?: ChannelPackagesQuery
): Promise<ChannelPackagesResponse> {
  const params = new URLSearchParams()
  
  if (query?.product_id) params.append('product_id', query.product_id)
  if (query?.channel_id) params.append('channel_id', query.channel_id)
  if (query?.status) params.append('status', query.status)
  
  const queryString = params.toString()
  const url = queryString 
    ? `/api/v1/channel-packages?${queryString}` 
    : '/api/v1/channel-packages'
  
  const response = await apiClient.get<ChannelPackagesResponse>(url)
  return response.data
}

/**
 * Get a single channel package
 */
export async function getChannelPackage(
  channelId: string,
  packageId: string
): Promise<ChannelPackageResponse> {
  const response = await apiClient.get<ChannelPackageResponse>(
    `${BASE_URL}/${channelId}/packages/${packageId}`
  )
  return response.data
}

// =============================================================================
// REGENERATION
// =============================================================================

/**
 * Regenerate a channel package
 */
export async function regenerateChannelPackage(
  channelId: string,
  packageId: string,
  reason?: string
): Promise<RegenerateResponse> {
  const response = await apiClient.post<RegenerateResponse>(
    `${BASE_URL}/${channelId}/packages/${packageId}/regenerate`,
    { reason }
  )
  return response.data
}

/**
 * Regenerate all channel packages for a product
 */
export async function regenerateAllChannelPackages(
  productId: string,
  reason?: string
): Promise<RegenerateAllResponse> {
  const response = await apiClient.post<RegenerateAllResponse>(
    `${PRODUCTS_URL}/${productId}/channel-packages/regenerate-all`,
    { reason }
  )
  return response.data
}

// =============================================================================
// RESOLVE CONTENT
// =============================================================================

/**
 * Resolve channel package content (get current snapshot)
 */
export async function resolveChannelPackage(
  channelId: string,
  packageId: string
): Promise<ResolveResponse> {
  const response = await apiClient.get<ResolveResponse>(
    `${BASE_URL}/${channelId}/packages/${packageId}/resolve`
  )
  return response.data
}

// =============================================================================
// STATUS HELPERS
// =============================================================================

/**
 * Get display status for a channel package
 */
export function getChannelPackageDisplayStatus(
  pkg: {
    regenerate_status: string
    last_regenerated_at: string | null
  },
  contentUpdatedAt?: string
): {
  status: 'current' | 'outdated' | 'processing' | 'error' | 'pending'
  icon: string
  color: string
  label: string
} {
  // Processing state
  if (pkg.regenerate_status === 'processing') {
    return {
      status: 'processing',
      icon: 'bi-arrow-repeat',
      color: 'text-info',
      label: 'Regeneriert...',
    }
  }
  
  // Pending state
  if (pkg.regenerate_status === 'pending') {
    return {
      status: 'pending',
      icon: 'bi-hourglass-split',
      color: 'text-muted',
      label: 'Ausstehend',
    }
  }
  
  // Error state
  if (pkg.regenerate_status === 'error') {
    return {
      status: 'error',
      icon: 'bi-x-circle-fill',
      color: 'text-danger',
      label: 'Fehler',
    }
  }
  
  // Check if outdated (content changed after last regeneration)
  if (contentUpdatedAt && pkg.last_regenerated_at) {
    const contentDate = new Date(contentUpdatedAt)
    const regenDate = new Date(pkg.last_regenerated_at)
    
    if (contentDate > regenDate) {
      return {
        status: 'outdated',
        icon: 'bi-exclamation-triangle-fill',
        color: 'text-warning',
        label: 'Veraltet',
      }
    }
  }
  
  // Current/completed state
  return {
    status: 'current',
    icon: 'bi-check-circle-fill',
    color: 'text-success',
    label: 'Aktuell',
  }
}

// =============================================================================
// CHANNELS
// =============================================================================

export interface ProductActiveChannel {
  id: string // Channel UUID
  channel_id: string // JTL format: "1-1-1", "5-30-0"
  channel_name: string
  channel_display_name?: string
  channel_type: string
  is_active: boolean
  image_count: number
  capabilities?: Record<string, unknown>
}

export interface ProductActiveChannelsResponse {
  success: boolean
  channels: ProductActiveChannel[]
  integration_id?: string
  product_external_id?: string
  integration_type?: string // Will be added by backend
}

/**
 * Get all active channels for a product's integration
 */
export async function getProductActiveChannels(
  productId: string
): Promise<ProductActiveChannelsResponse> {
  const response = await apiClient.get<ProductActiveChannelsResponse>(
    `${PRODUCTS_URL}/${productId}/active-channels`
  )
  return response.data
}

/**
 * Create a channel package for a product + channel
 */
export async function createChannelPackage(
  productId: string,
  channelId: string
): Promise<ChannelPackageResponse> {
  const response = await apiClient.post<ChannelPackageResponse>(
    `${PRODUCTS_URL}/${productId}/channel-packages`,
    { channel_id: channelId }
  )
  return response.data
}


